/**
 * IrishFundsExecutives Component - By John Bellew - Infinia.ie - last updated 15/07/2025
 * 
 * A specialized people grid component for displaying Irish Funds executive team members.
 * Features include:
 * - Dynamic color mapping for executive departments
 * - Configurable filtering system for departmental categories
 * - 5-column responsive grid layout optimized for executive display
 * - Role information display for each executive
 * - Profile-specific modal integration for executive profiles
 * - IFIA brand color scheme with automatic color assignment
 * - Production-ready with comprehensive executive data handling
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of executive objects with dept, name, role, and other details
 * @returns {JSX.Element} Irish Funds executives grid component
 */
import React, { useMemo } from 'react';
import PeopleGrid from '../Common/PeopleGrid';

/**
 * IrishFundsExecutives Component
 * 
 * Displays Irish Funds executive team members in a grid layout with filtering 
 * and color-coded departmental categories. Optimized for performance with 
 * memoized color mapping and minimal re-renders.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Executive data array with departmental information
 * @returns {JSX.Element} Irish Funds executives component
 */
const IrishFundsExecutives = ({ data }) => {
    // Memoize color mapping based on stable departmental data
    const colorMap = useMemo(() => {
        if (!data || data.length === 0) return {};
        
        const colors = [
            'var(--ifia-green)',
            'var(--ifia-orange)',
            'var(--ifia-blue)',
            'var(--ifia-purple)',
            'var(--ifia-dark-green)',
            'var(--ifia-lightblue)'
        ];
        
        // Use sorted unique departments for consistent color assignment
        const uniqueFilter = [...new Set(data.map(member => member.dept))].sort();
        return uniqueFilter.reduce((acc, category, index) => {
            acc[category] = colors[index % colors.length];
            return acc;
        }, {});
    }, [data?.length, data?.map(m => m.dept).join(',')]);

    return (
        <PeopleGrid 
            data={data}
            filterField="dept"
            colorMap={colorMap}
            gridColumns={5}
            showRole={true}
            showFilters={true}
            modalType="profile"
        />
    );
};

export default IrishFundsExecutives;